/**
 * The customer detail panel — the supplier-management view of one account.
 *
 * As with Hotels, this is not new data: `/api/customers` already returned every
 * field below while the table showed six of them and the page's own interface
 * declared eight. Opening a row now answers "who owns this account, how big is
 * it, how long have we had it" without leaving the queue for the CRM profile.
 *
 * DELIBERATELY OMITTED: `cust_ae_email`, `cust_csm_email`, `combined_iata`,
 * `health_band` and `health_score`. The route returns all five hardcoded to
 * null for every row, so rendering them would print a permanent em-dash that
 * reads as "we don't know this customer's health score" when the truth is that
 * the field was never wired to a source. A field that can never be filled is
 * worse than an absent one — see `Field`, which treats a blank as a signal.
 *
 * The `Customer` type lives here so the row and the panel cannot disagree about
 * the payload shape, matching AgreementDetail and HotelDetail.
 */
import { Link } from 'react-router';
import { Field, Group } from '@/features/crm/components/DetailPanel';
import { fmtMaybeCalendarDate } from '@/features/crm/lib/dates';
import {
  AgreementRollupSection,
  type AgreementRollup,
} from '../../components/AgreementRollup';
import { CustomerRacs } from '@/features/hotel-supplier/components/CustomerRacs';

export interface Customer {
  customer_id: string;
  name: string;
  account_number: string;
  domain: string | null;
  status: string | null;
  cust_ae: string | null;
  cust_csm: string | null;
  cust_ops_mgr: string | null;
  industry: string | null;
  size: string | null;
  city: string | null;
  state: string | null;
  total_spend_12m: number | null;
  travelers_12m: number | null;
  btp_enabled_hint: boolean | null;
  key_contact: string | null;
  key_contact_email: string | null;
  /** Date-only ('YYYY-MM-DD'), the tenure source. Format as a calendar date. */
  customer_start_date: string | null;
  is_global: boolean;
  has_partner_agency: boolean;
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** Null-preserving so `Field` owns the em-dash. An explicit null test, because
 *  `!v` would blank a real zero — and "$0 in the last 12 months" is a finding. */
function fmtSpend(v: number | null): string | null {
  return v == null ? null : usd.format(v);
}

function fmtCount(v: number | null): string | null {
  return v == null ? null : v.toLocaleString('en-US');
}

/** Booleans are never null on these two columns, so Yes/No is always honest. */
function yesNo(v: boolean): string {
  return v ? 'Yes' : 'No';
}

export function CustomerDetail({
  agreements,
  customer: c,
}: {
  /**
   * This customer's agreements. Arrives from /api/btp/customer-agreements — a
   * SEPARATE request from the customer list, because /api/customers is
   * CRM-owned, shared, and called with ?limit=5000 by the CRM list page (see
   * that route's docstring). Undefined while that second request is in flight;
   * the rollup only shows inside an opened row, so it has effectively always
   * landed by the time anyone sees this.
   */
  agreements?: AgreementRollup;
  customer: Customer;
}) {
  return (
    <div className="detail-panel">
      <Group label="Account" testId="customer-detail-account">
        <dl className="detail-grid">
          <Field label="Customer" value={c.name} />
          <Field label="Account number" mono value={c.account_number} />
          {/* The id support pastes into a ticket, and the key every HSP table
              joins on. Worth being copyable rather than only a hover title. */}
          <Field label="Customer ID" mono value={c.customer_id} />
          <Field label="Status" value={c.status} />
          <Field label="Industry" value={c.industry} />
          <Field label="Size" value={c.size} />
          <Field label="City" value={c.city} />
          <Field label="State" value={c.state} />
          <Field label="Domain">
            {c.domain ? (
              <a
                href={`https://${c.domain}`}
                rel="noreferrer noopener"
                target="_blank"
              >
                {c.domain}
              </a>
            ) : null}
          </Field>
          {/* Both bear directly on how an agreement gets negotiated: a global
              program means rates may need to load in more than one market, and a
              partner agency means we are not the only TMC touching the booking. */}
          <Field label="Global program" value={yesNo(c.is_global)} />
          <Field label="Partner agency" value={yesNo(c.has_partner_agency)} />
        </dl>
      </Group>

      {/*
       * RATE ACCESS CODES. Asked for in three consecutive sessions with the
       * reason attached — Stacie, 2026-09-03: "All of those are necessary for
       * the GDS rate loading instructions." The RLI has to name which RAC the
       * negotiated rate loads against.
       *
       * Directly after Account and before ownership, because it is reference
       * data someone copies while writing an instruction, not something they
       * scroll to.
       */}
      <Group label="Rate access codes" testId="customer-detail-racs">
        <CustomerRacs customerId={c.customer_id} testId="customer-racs" />
      </Group>

      <Group label="Who owns it" testId="customer-detail-owners">
        <dl className="detail-grid">
          <Field label="Account executive" value={c.cust_ae} />
          <Field label="Client success manager" value={c.cust_csm} />
          <Field label="Operations manager" value={c.cust_ops_mgr} />
          <Field label="Key contact" value={c.key_contact} />
          <Field label="Key contact email">
            {c.key_contact_email ? (
              <a href={`mailto:${c.key_contact_email}`}>
                {c.key_contact_email}
              </a>
            ) : null}
          </Field>
        </dl>
      </Group>

      <Group label="Program" testId="customer-detail-program">
        <dl className="detail-grid">
          {/* Trailing-twelve-month figures, per the source columns' own naming.
              They size the account for negotiation — how much leverage this
              customer's volume actually carries with a property. */}
          <Field label="Spend (12m)" value={fmtSpend(c.total_spend_12m)} />
          <Field label="Travelers (12m)" value={fmtCount(c.travelers_12m)} />
          <Field
            label="Customer since"
            value={fmtMaybeCalendarDate(c.customer_start_date)}
          />
          <Field label="Supplier Management">
            {c.btp_enabled_hint == null
              ? null
              : c.btp_enabled_hint
                ? 'Enrolled'
                : 'Not enrolled'}
          </Field>
        </dl>
      </Group>

      {/* The "work outward" half of this page's purpose: from a customer to the
          agreements in flight for them. `?customer_id=` is a filter the
          Agreements page really applies, so "See all N" is a working link.

          `rollup` is passed through UNDEFINED on purpose when the second request
          has not resolved or has failed. Collapsing that into an empty rollup is
          what made this panel claim "nothing has been sourced" over a failed
          fetch — a definitive answer given without the data. */}
      <AgreementRollupSection
        emptyMessage="No agreements for this customer yet — nothing has been sourced against their program."
        moreHref={`/hotel-supplier/agreements?customer_id=${encodeURIComponent(
          c.customer_id,
        )}`}
        rollup={agreements}
        show="property"
        testId="customer-detail-agreements"
      />

      {/* The way out of the queue and into the canonical record. The panel
          answers the questions asked while working a list; anything beyond that
          belongs to the CRM profile, which owns customer identity. */}
      <Group label="Elsewhere" testId="customer-detail-links">
        <ul className="detail-list">
          <li>
            <Link to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}>
              Open the full customer profile
            </Link>
          </li>
          <li>
            {/* Deliberately carries `customer_id`, which the Agreements page
                forwards to the API and shows as a clearable chip. It lands on
                whichever stage that page defaults to, with the stage pills and
                the customer chip both visible — so the scope of what is being
                shown is on screen rather than implied. */}
            <Link
              data-testid={`supplier-customer-agreements-${c.customer_id}`}
              to={`/hotel-supplier/agreements?customer_id=${encodeURIComponent(
                c.customer_id,
              )}`}
            >
              Agreements for this customer
            </Link>
          </li>
        </ul>
      </Group>
    </div>
  );
}
